import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getSyncTemplates, getSegmentationOptions, getAggressivenessOptions,
    simulateNewRule, createPriceRule, getGranulation
} from '@/services/api';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react';
import type { SimulateNewRuleRequest, SimulateNewRuleResponse } from '@/types/price-rules';

import StepDomain from './components/create/StepDomain';
import StepSegmentation from './components/create/StepSegmentation';
import StepConfig from './components/create/StepConfig';
import StepSimulation from './components/create/StepSimulation';

const STEPS = [
    { id: 1, name: 'Domínio/Template' },
    { id: 2, name: 'Segmentação' },
    { id: 3, name: 'Configuração' },
    { id: 4, name: 'Simulação' },
];

export default function CreatePriceRule() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Pega domínio da URL se existir (opcional)
    const urlDomainId = searchParams.get('domain_id') ? Number(searchParams.get('domain_id')) : null;
    const urlDomainName = searchParams.get('domain_name') || '';

    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState<Partial<SimulateNewRuleRequest>>({
        domain_id: urlDomainId || undefined,
        domain_name: urlDomainName,
        country: '*',
        ad_unit: '*',
        device: '*',
        custom_key: null,
        custom_value: null,
        pmr_target: 70,
        sync_interval_minutes: 1440,
        ecpm_reference: 'rule_price',
        template_id: null,
    });
    const [simulation, setSimulation] = useState<SimulateNewRuleResponse | null>(null);
    const [manualPrice, setManualPrice] = useState<number | null>(null);
    const [isSimulating, setIsSimulating] = useState(false);

    const { data: templates } = useQuery({
        queryKey: ['sync-templates'],
        queryFn: () => getSyncTemplates(),
    });

    const { data: segmentationOptions } = useQuery({
        queryKey: ['segmentation-options'],
        queryFn: getSegmentationOptions,
    });

    const { data: aggressivenessOptions } = useQuery({
        queryKey: ['aggressiveness-options'],
        queryFn: getAggressivenessOptions,
    });

    const { data: granulation } = useQuery({
        queryKey: ['granulation'],
        queryFn: getGranulation,
    });

    const simulateMutation = useMutation({
        mutationFn: () => simulateNewRule(formData as SimulateNewRuleRequest),
        onSuccess: (data) => {
            setSimulation(data);
            setManualPrice(null);
            setIsSimulating(false);
            setCurrentStep(4);
        },
        onError: (error: any) => {
            setIsSimulating(false);
            toast({ variant: 'destructive', title: 'Erro ao simular', description: error.response?.data?.detail || 'Erro ao consultar GAM.' });
        },
    });

    const createMutation = useMutation({
        mutationFn: () => {
            const finalPrice = manualPrice ?? simulation?.suggested_price ?? 0;
            return createPriceRule({
                domain_id: formData.domain_id!,
                template_id: formData.template_id,
                country: formData.country,
                ad_unit: formData.ad_unit,
                device: formData.device as any,
                custom_key: formData.custom_key,
                custom_value: formData.custom_value,
                price: finalPrice,
                pmr_target: formData.pmr_target,
                sync_interval_minutes: formData.sync_interval_minutes,
                ecpm_reference: formData.ecpm_reference,
            });
        },
        onSuccess: () => {
            toast({ title: 'Regra criada!', description: 'A regra foi criada com sucesso.' });
            queryClient.invalidateQueries({ queryKey: ['price-rules'] });
            navigate('/price-rules');
        },
        onError: (error: any) => {
            toast({ variant: 'destructive', title: 'Erro ao criar', description: error.response?.data?.detail || 'Erro ao criar regra.' });
        },
    });

    const handleNext = () => {
        if (currentStep === 3) {
            setIsSimulating(true);
            simulateMutation.mutate();
        } else if (currentStep < 4) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
            if (currentStep === 4) setSimulation(null);
        }
    };

    const handleCreate = () => {
        const finalPrice = manualPrice ?? simulation?.suggested_price ?? 0;
        if (finalPrice <= 0) {
            toast({ variant: 'destructive', title: 'Price inválido', description: 'O price deve ser maior que zero.' });
            return;
        }
        createMutation.mutate();
    };

    const canProceed = () => {
        switch (currentStep) {
            case 1: return !!formData.domain_id && !!formData.template_id;
            case 2: return true;
            case 3: return (formData.pmr_target || 0) > 0 && (formData.sync_interval_minutes || 0) > 0;
            case 4: return simulation?.has_data && simulation?.can_create;
            default: return false;
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => navigate('/price-rules')}>
                    <ArrowLeft className="mr-2 h-4 w-4" />Voltar
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Criar Nova Regra</h1>
                    {formData.domain_name && <p className="text-gray-600">{formData.domain_name}</p>}
                </div>
            </div>

            {/* Progress */}
            <div className="bg-white rounded-lg shadow p-6">
                <ol className="flex items-center">
                    {STEPS.map((step, idx) => (
                        <li key={step.id} className={`relative ${idx !== STEPS.length - 1 ? 'pr-8 sm:pr-20 flex-1' : ''}`}>
                            <div className="flex items-center">
                                <div className={`flex h-8 w-8 items-center justify-center rounded-full ${
                                    step.id < currentStep ? 'bg-green-600' : step.id === currentStep ? 'bg-blue-600' : 'bg-gray-200'
                                }`}>
                                    {step.id < currentStep ? (
                                        <Check className="h-5 w-5 text-white" />
                                    ) : (
                                        <span className={`text-sm font-medium ${step.id === currentStep ? 'text-white' : 'text-gray-500'}`}>{step.id}</span>
                                    )}
                                </div>
                                <span className={`ml-4 text-sm font-medium hidden sm:block ${step.id <= currentStep ? 'text-gray-900' : 'text-gray-500'}`}>
                                    {step.name}
                                </span>
                            </div>
                            {idx !== STEPS.length - 1 && (
                                <div className={`absolute top-4 left-8 -ml-px h-0.5 w-full sm:w-20 ${step.id < currentStep ? 'bg-green-600' : 'bg-gray-200'}`} />
                            )}
                        </li>
                    ))}
                </ol>
            </div>

            {/* Content */}
            <div className="bg-white rounded-lg shadow p-6 min-h-[400px]">
                {currentStep === 1 && <StepDomain templates={templates || []} formData={formData} setFormData={setFormData} />}
                {currentStep === 2 && segmentationOptions && <StepSegmentation formData={formData} setFormData={setFormData} segmentationOptions={segmentationOptions} />}
                {currentStep === 3 && aggressivenessOptions && <StepConfig formData={formData} setFormData={setFormData} aggressivenessOptions={aggressivenessOptions} />}
                {currentStep === 4 && <StepSimulation simulation={simulation} formData={formData} manualPrice={manualPrice} setManualPrice={setManualPrice} granulation={granulation} isLoading={isSimulating} segmentationOptions={segmentationOptions} />}
            </div>

            {/* Navigation */}
            <div className="flex justify-between">
                <Button variant="outline" onClick={handleBack} disabled={currentStep === 1 || isSimulating || createMutation.isPending}>
                    <ArrowLeft className="mr-2 h-4 w-4" />Voltar
                </Button>

                {currentStep < 4 ? (
                    <Button onClick={handleNext} disabled={!canProceed() || isSimulating}>
                        {isSimulating ? (
                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Consultando GAM...</>
                        ) : (
                            <>{currentStep === 3 ? 'Simular' : 'Próximo'}<ArrowRight className="ml-2 h-4 w-4" /></>
                        )}
                    </Button>
                ) : (
                    <Button onClick={handleCreate} disabled={!canProceed() || createMutation.isPending} className="bg-green-600 hover:bg-green-700">
                        {createMutation.isPending ? (
                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Criando...</>
                        ) : (
                            <><Check className="mr-2 h-4 w-4" />Criar Regra</>
                        )}
                    </Button>
                )}
            </div>
        </div>
    );
}